/*
 * Office.js selection edits, ported from taskpane.js (replaceSelectionText /
 * the accept-occurrence write). Tracked-changes handling and reverse-order
 * application are correctness-critical and kept unchanged. Translated error
 * messages are passed in so this module stays free of i18n coupling.
 */

import type { DocSuggestion, DocxReviewMode } from "../state/types";

/** Replace the whole current selection with `text` (direct overwrite). */
export async function insertSelectionText(text: string): Promise<void> {
  await Word.run(async (ctx) => {
    const sel = ctx.document.getSelection();
    sel.insertText(text, Word.InsertLocation.replace);
    await ctx.sync();
  });
}

/**
 * Replace a single word occurrence inside the current selection, touching only
 * that word's range so surrounding text and formatting are preserved.
 * `occurrenceIndex` is the 0-based index of `word` among its matches within the
 * selection (in document order). Throws `notFoundMessage` if the match is gone.
 */
export async function replaceOccurrenceInSelection(
  word: string,
  replacement: string,
  occurrenceIndex: number,
  notFoundMessage: string
): Promise<void> {
  await Word.run(async (ctx) => {
    const sel = ctx.document.getSelection();
    const searchResult = sel.search(word, { matchCase: true });
    searchResult.load("items");
    await ctx.sync();

    const items = searchResult.items;
    if (!items || items.length <= occurrenceIndex) {
      throw new Error(notFoundMessage);
    }

    items[occurrenceIndex].insertText(replacement, Word.InsertLocation.replace);
    await ctx.sync();
  });
}

/**
 * Replace the selection, optionally as tracked changes mapped to per-change
 * ranges. Falls back to a direct overwrite when not tracked or no change list.
 */
export async function replaceSelectionText(
  text: string,
  reviewMode: DocxReviewMode,
  changes: DocSuggestion[] | undefined,
  originalText: string,
  trackedUnsupportedMessage: string
): Promise<void> {
  if (reviewMode !== "tracked" || !changes || changes.length === 0) {
    await insertSelectionText(text);
    return;
  }

  if (!Office.context.requirements.isSetSupported("WordApi", "1.4")) {
    throw new Error(trackedUnsupportedMessage);
  }

  await Word.run(async (ctx) => {
    const document = ctx.document;
    const sel = document.getSelection();

    // Prepare search tasks to find the exact occurrence of each change.
    const searchTasks = changes.map((change) => {
      const beforePosition = originalText.substring(0, change.position ?? 0);
      const before = String(change.before ?? "");
      let occurrenceIndex = 0;
      let pos = beforePosition.indexOf(before);
      while (pos !== -1) {
        occurrenceIndex++;
        pos = beforePosition.indexOf(before, pos + 1);
      }

      const searchResult = sel.search(before, { matchCase: true });
      searchResult.load("items");
      return { change, occurrenceIndex, searchResult };
    });

    await ctx.sync();

    document.load("changeTrackingMode");
    await ctx.sync();

    const previousMode = document.changeTrackingMode;
    try {
      document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      // Process changes in reverse so edits don't shift subsequent occurrences.
      for (let i = searchTasks.length - 1; i >= 0; i--) {
        const task = searchTasks[i];
        const items = task.searchResult.items;
        if (items && items.length > task.occurrenceIndex) {
          const targetRange = items[task.occurrenceIndex];
          const afterCursor = targetRange.getRange(Word.RangeLocation.after);
          afterCursor.insertText(String(task.change.after ?? ""), Word.InsertLocation.before);
          targetRange.delete();
        }
      }
      await ctx.sync();
    } finally {
      document.changeTrackingMode = previousMode || Word.ChangeTrackingMode.off;
      await ctx.sync();
    }
  });
}
